/* @jsx h */
import { defineComponent, h } from "vue";
import {
  SchemaForm,
  type FormOptions,
  type StructLocals,
} from "@lemmonhq/uikit";
import {
  LemmonFeatureFlagsSchema,
  type LemmonFeatureFlags,
} from "@lemmonhq/types";

// ---------------------------------------------------------------------------
// Human-readable metadata for each flag key
// ---------------------------------------------------------------------------

const FLAG_META: Record<
  keyof LemmonFeatureFlags,
  { label: string; description: string }
> = {
  la_suite_default_on: {
    label: "La Suite — Default On",
    description: "Enable La Suite table experience by default for all orders.",
  },
  payment_integration_v2: {
    label: "Payment Integration v2",
    description: "Use the redesigned payment integration flow.",
  },
  payment_integration_v2_tips: {
    label: "Payment Integration v2 — Tips",
    description: "Show the tip screen before tap-to-pay (SoftPOS) payments.",
  },
  nakka_browser: {
    label: "Nakka — Browser (legacy)",
    description:
      "Run Nakka terminal payments in the browser the old way instead of the payment workflow (DBOS / Restate / Cloudflare).",
  },
  mobile_pos_only_dine_in: {
    label: "Mobile POS — Dine-In Only",
    description: "Restrict the mobile POS to dine-in orders only.",
  },
  "discounts-v2": {
    label: "Discounts v2",
    description: "Enable the new discounts engine.",
  },
  print_job_tracking: {
    label: "Print Job Tracking",
    description:
      "Record the outcome of every kitchen ticket sent from this restaurant's devices.",
  },
  native_print: {
    label: "Native Printing",
    description:
      "Print kitchen tickets through the mobile app itself (app 1.3.0+), so they finish and are reported even if the page reloads or the device is locked.",
  },
  payrexx_new_platform: {
    label: "Payrexx — New Platform",
    description:
      "Route Payrexx terminal (ECR) calls to the new platform host (api.lemmon-pos.ch) instead of the legacy pay.thelemmon.ch proxy.",
  },
};

// ---------------------------------------------------------------------------
// A bare struct template: no fieldset border, just stacks fields
// ---------------------------------------------------------------------------

const bareStructTemplate = (locals: StructLocals) => (
  <div class="flex flex-col divide-y divide-base-200">
    {locals.order.map((key) => {
      const renderFn = locals.inputs[key];
      return renderFn ? renderFn() : null;
    })}
  </div>
);

// ---------------------------------------------------------------------------
// Build SchemaForm options from FLAG_META
// ---------------------------------------------------------------------------

const formOptions: FormOptions = {
  auto: "none",
  template: bareStructTemplate,
  fields: Object.fromEntries(
    Object.entries(FLAG_META).map(([key, { label, description }]) => [
      key,
      {
        label,
        toggle: true,
        config: { description },
      },
    ]),
  ),
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const FeatureFlagsSettings = defineComponent<{
  flags: LemmonFeatureFlags;
  onUpdate: (flags: LemmonFeatureFlags) => void;
}>({
  name: "FeatureFlagsSettings",
  props: ["flags", "onUpdate"],
  setup(props) {
    return () => (
      <section class="bg-base-100 l-box-shadow  rounded-xl p-6 shadow-sm border ">
        <header class="mb-6 space-y-1">
          <div class="flex items-center gap-3">
            <div class="p-2 bg-warning/10 rounded-xl">
              <span class="material-symbols-outlined text-warning text-xl">
                flag
              </span>
            </div>
            <div>
              <h2 class="text-xl font-semibold text-accent">Feature Flags</h2>
              <p class="text-sm text-base-content">
                Enable or disable experimental and rolling-out features for this
                restaurant.
              </p>
            </div>
          </div>
        </header>

        <SchemaForm
          schema={LemmonFeatureFlagsSchema}
          options={formOptions}
          value={props.flags ?? {}}
          onChange={(v: LemmonFeatureFlags) => props.onUpdate(v)}
        />
      </section>
    );
  },
});
